// src/components/ProductOptionsModal.tsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Product, CartItem } from '../utils/types'; // Ensure the types are correct
import { fetchProductSpecifications } from '../utils/api'; // Adjust import based on your API utility
import { useCart } from '../context/CartContext'; // Import cart context
import { generateUniqueKey } from '../utils/general';
import { useSettings } from '../context/SettingsContext';
import { useSpring, animated } from 'react-spring';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useLocation } from 'react-router-dom';
import { displayPrice } from '../utils/general';


interface ProductOptionsModalProps {
    product: Product; // Expecting a complete product object
    vendorId: number;
    isOpen: boolean;
    onClose: () => void;
    initialQuantity: number; // Receive initial quantity
    cartItem?: CartItem; // New prop to pass the current cart item when editing
    isEditMode?: boolean; // To check if we're in edit mode
    fromCart: boolean // Enable edit mode if from Cart

}

const ProductOptionsModal: React.FC<ProductOptionsModalProps> = ({
    product,
    vendorId,
    isOpen,
    onClose,
    initialQuantity,
    cartItem,
    isEditMode = false,
    fromCart = false,
}) => {

    // console.log('isEditMode at 34 >> ', isEditMode)

    const { settings } = useSettings();

    // const navigate = useNavigate();

    // const [ref, inView] = useInView({ threshold: 0.1 });

    // // Animation for flipping effect
    // const props = useSpring({
    //     transform: inView ? 'rotateY(0deg)' : 'rotateY(180deg)',
    //     config: { tension: 300, friction: 20 },
    // });

    const [error, setError] = useState<string | null>(null);
    const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: any }>({});
    const [specifications, setSpecifications] = useState<any[]>([]);
    const { addToCart, updateThisCart, removeFromCart, cartItems } = useCart();

    const modalRef = useRef<HTMLDivElement>(null); // Ref for modal container

    const [totalPrice, setTotalPrice] = useState<number>(product.price);
    const [quantity, setQuantity] = useState<number>(initialQuantity);

    const [isClosing, setIsClosing] = useState(false);

    // Handle close animation
    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 400); // Match the duration with animation
    };

    // Close modal if clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                handleClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);


    useEffect(() => {
        if (isOpen) {
            setSelectedOptions({});
            setError(null); // Reset error state

            const loadSpecifications = async () => {
                try {
                    const data = await fetchProductSpecifications(vendorId, product.id);
                    // console.log('fetchProductSpecifications:', data);

                    setSpecifications(data);
                } catch (error) {
                    console.error("Error loading specifications:", error);
                    setError("Error loading specifications");
                }
            };

            loadSpecifications();
        }
    }, [isOpen, vendorId, product.id]);

    const calculateTotalPrice = useCallback((selectedOptions: any, quantity: number) => {
        let total = typeof product.price === 'string' ? parseFloat(product.price) : product.price;

        // Calculate total based on selected options
        ['add_ons', 'toppings', 'specifications'].forEach(type => {
            if (selectedOptions[type]) {
                selectedOptions[type].forEach((option: any) => {
                    // console.log('selectedOptions[type].forEach((option: any):', selectedOptions[type], option.price);

                    total += typeof option.price === 'string' ? parseFloat(option.price) : option.price;
                });
            }
        });

        setTotalPrice(total * quantity);
    }, [product.price]);

    useEffect(() => {
        calculateTotalPrice(selectedOptions, quantity);
    }, [selectedOptions, quantity, calculateTotalPrice]);


    useEffect(() => {
        const hasOptions = cartItem ? Object.keys(cartItem.options).some(
            (key) => cartItem.options[key as keyof typeof cartItem.options].length > 0
        ) : false;

        const addOns = product.add_ons ? JSON.parse(product.add_ons) : [];
        const toppings = product.toppings ? JSON.parse(product.toppings) : [];


        const hasAddOns = addOns.length > 0;
        const hasToppings = toppings.length > 0;

        if (fromCart) {
            // console.log('fromCart', fromCart);

            if (isEditMode && cartItem) { // && fromCart && hasOptions) {
                // Set selected options from the cart item
                setSelectedOptions(cartItem.options);

                // Ensure quantity is at least 1
                // setQuantity(Math.max(cartItem.quantity || 0, 1));
                setQuantity(Math.max(cartItem.quantity || 0, 1));
            } else {
                // Reset options and set initial quantity
                setSelectedOptions({});
                // setQuantity(initialQuantity >= 1 ? initialQuantity : 1);
                setQuantity(1);
            }

        } else {

            // abb 250104 for NOT fromCart
            // console.log('fromCart', fromCart);

            if (hasAddOns || hasToppings || specifications) {

                // Reset options and set initial quantity
                setSelectedOptions({});
                setQuantity(1);
            } else {

                if (isEditMode && cartItem) { // && fromCart && hasOptions) {
                    // Set selected options from the cart item
                    setSelectedOptions(cartItem.options);

                    // Ensure quantity is at least 1
                    setQuantity(Math.max(cartItem.quantity || 0, 1));
                }
            }

        }
    }, [isEditMode, cartItem, initialQuantity, fromCart]);


    const handleOptionChange = (
        type: string,
        keyid: number,
        key: string,
        name: string,
        price: number,
        specType: string
    ) => {
        const formattedName = name.replace(/\s+/g, '_');
        const generatedKey = `${product.id}${formattedName}`;

        setSelectedOptions(prev => {

            const updatedOptions = { ...prev };

            // Ensure the correct structure exists for the option type
            if (!updatedOptions[type]) updatedOptions[type] = [];

            if (specType === 'checkbox') {
                // const existingIndex = updatedOptions[type].findIndex((option: any) => option.key === generatedKey);

                // console.log('i am checkbox', existingIndex, updatedOptions, type, generatedKey, name, price)
            } else {
                // **Filter out existing selections for the same `keyid`** (in this case, "sugar")
                //use for radio, remove current when select new one 24-10-24
                updatedOptions[type] = updatedOptions[type].filter((opt: any) => opt.keyid !== keyid);
            }

            // Now proceed with adding the new selection based on `specType`
            if (specType === 'radio') {
                // **Add the new option as the only selected option for radios**
                updatedOptions[type].push({ keyid, key: generatedKey, name, price });
            } else {
                // Checkbox behavior: toggle the selection
                const existingIndex = updatedOptions[type].findIndex((option: any) => option.key === generatedKey);

                if (existingIndex === -1) {
                    // Add option if not selected
                    updatedOptions[type].push({ keyid, key: generatedKey, name, price });
                } else {
                    // Remove option if already selected
                    updatedOptions[type].splice(existingIndex, 1);
                }
            }

            return updatedOptions;
        });
    };

    // if (!isOpen) return null;
    if (!isOpen && !isClosing) return null;


    /* below is initial NOT fromCart if got options is ADD else EDIT */
    const addOns = product.add_ons ? JSON.parse(product.add_ons) : [];
    const toppings = product.toppings ? JSON.parse(product.toppings) : [];

    const hasAddOns = addOns.length > 0;
    const hasToppings = toppings.length > 0;

    if (!fromCart) {

        if ((addOns && addOns.length > 0) ||
            (toppings && toppings.length > 0) ||
            (specifications && specifications.length > 0)) {

            isEditMode = false;


        } else {
            if (isEditMode && cartItem) {
                isEditMode = false; // abb 250107 turn to true if not fromCart need change qty instead of add qty
            } else {
                isEditMode = false;
            }
        }

    }
    /* above is initial NOT fromCart if got options is ADD else EDIT */


    //handle submit must be last abb 250103

    const handleSubmit = () => {

        // console.log('handleSubmit', selectedOptions, quantity, totalPrice, product);

        // Generate a new productKey if not in edit mode or if there are selected options
        const productKey = isEditMode && Object.keys(selectedOptions).length === 0 ? cartItem?.productKey : generateUniqueKey(product.id, selectedOptions);

        const cartData: CartItem = {
            productKey: productKey || generateUniqueKey(product.id, selectedOptions),
            productId: product.id,
            vendorId: product.vendor_id,
            name: product.name,
            price: product.price,
            quantity: quantity, // Set the quantity directly
            options: {
                add_ons: selectedOptions.add_ons || [],
                toppings: selectedOptions.toppings || [],
                specifications: selectedOptions.specifications || [],
            },
            totalPrice: totalPrice,
            product: product,
            checkedOut: false,
        };

        // console.log('updating 296 ', cartItems, productKey, cartData);

        if (isEditMode && Object.keys(selectedOptions).length === 0) {
            updateThisCart(cartData);
        } else {
            // // Check if there is an existing item with the same product ID and options
            // const currentCartItem = cartItems.find(cartItem =>
            //     cartItem.productId === product.id &&
            //     JSON.stringify(cartItem.options) === JSON.stringify(selectedOptions)
            // );

            // Check if there is an existing item with the same product ID and options
            const existingCartItem = cartItems.find(cartItem =>
                cartItem.productId === product.id &&
                JSON.stringify(cartItem.options) === JSON.stringify(selectedOptions)
            );

            if (existingCartItem) {
                // console.log('if (existingCartItem) { updating >> ', product, productKey, cartData, existingCartItem);

                // Combine quantities and update the existing item
                const updatedCartItem = {
                    ...existingCartItem,
                    quantity: isEditMode ? quantity : existingCartItem.quantity + quantity, // Set quantity directly if in edit mode
                    totalPrice: (existingCartItem.totalPrice / existingCartItem.quantity) * (isEditMode ? quantity : existingCartItem.quantity + quantity) // Update total price based on new quantity
                };
                updateThisCart(updatedCartItem);
                // console.log('if (existingCartItem) >> ', isEditMode)
            } else {

                // // console.log('adding', cartItem, cartItem?.productKey, 'editedoOldItem', productKey, isEditMode);
                // const hasOptions = cartItem ? Object.keys(cartItem.options).some(
                //     (key) => cartItem.options[key as keyof typeof cartItem.options].length > 0
                // ) : false;

                const addOns = product.add_ons ? JSON.parse(product.add_ons) : [];
                const toppings = product.toppings ? JSON.parse(product.toppings) : [];


                const hasAddOns = addOns.length > 0;
                const hasToppings = toppings.length > 0;

                if (!fromCart) {

                    // console.log('!fromCart passed 340 cartData >> ', cartData);

                    const existingItemOptionZeroNotFromCart = cartItems.find(cartItem =>
                        cartItem.productId === cartData.productId &&
                        (
                            (cartItem.options.add_ons?.length === 0 && cartData.options.add_ons?.length === 0) ||
                            (cartItem.options.toppings?.length === 0 && cartData.options.toppings?.length === 0) ||
                            (cartItem.options.specifications?.length === 0 && cartData.options.specifications?.length === 0)


                            // (cartItem && (!hasAddOns && !hasToppings && !specifications))

                        )
                    );

                    // console.log('existingItemOptionZeroNotFromCart 339 >> ', existingItemOptionZeroNotFromCart, cartItems, cartData, productKey);

                    if (existingItemOptionZeroNotFromCart === cartData) {
                        // console.log('updating', existingItemOptionZeroNotFromCart, cartData);

                        // Combine quantities and update the existing item
                        const updatedCartItem = {
                            ...existingItemOptionZeroNotFromCart,
                            quantity: isEditMode ? quantity : existingItemOptionZeroNotFromCart.quantity + quantity, // Set quantity directly if in edit mode
                            totalPrice: (existingItemOptionZeroNotFromCart.totalPrice / existingItemOptionZeroNotFromCart.quantity) * (isEditMode ? quantity : existingItemOptionZeroNotFromCart.quantity + quantity) // Update total price based on new quantity
                        };
                        updateThisCart(updatedCartItem);
                    }

                } else {

                    if ((cartItem && isEditMode && (hasAddOns || hasToppings || specifications))) {
                        const editedoOldItem = cartItem?.productKey;

                        // Remove the old cart item if it exists
                        if (editedoOldItem) {
                            removeFromCart(editedoOldItem);
                        }

                    }
                }


                const ItemOptionZeroNotFromCart = cartItems.find(cartItem =>
                    cartItem.productId === product.id &&
                    // cartItem.productKey === productKey &&
                    (
                        // (cartItem.options.add_ons.length === 0 && cartData.options.add_ons.length === 0) &&
                        // (cartItem.options.toppings.length === 0 && cartData.options.toppings.length === 0) &&
                        // (cartItem.options.specifications.length === 0 && cartData.options.specifications.length === 0)

                        (cartItem && (hasAddOns && hasToppings && specifications))
                    )
                );

                if (ItemOptionZeroNotFromCart) {
                    //
                    // console.log('if (ItemOptionZeroNotFromCart) { ln 397 >> ',
                    // cartData.options.add_ons,
                    // cartData.options.toppings,
                    // cartData.options.specifications);

                    if (
                        cartData.options.add_ons.length !== 0 ||
                        cartData.options.toppings.length !== 0 ||
                        cartData.options.specifications.length !== 0
                    ) {
                        // addToCart(ItemOptionZeroNotFromCart);
                        addToCart(cartData);

                        // } else {

                        // console.log('ItemOptionZeroNotFromCart ln 409 >> ', ItemOptionZeroNotFromCart);


                    } else {
                        // Add the new item to the cart
                        addToCart(cartData);
                        // console.log('addToCart(cartData); >> isEditMode is ', isEditMode)
                    }

                } else {
                    // Add the new item to the cart
                    addToCart(cartData);
                    // console.log('addToCart(cartData); >> isEditMode is ', isEditMode)
                }

            }
        }


        // if (isEditMode) {
        //     updateThisCart(cartData);
        // } else {
        //     addToCart(cartData);
        // }

        // Reset options and set initial quantity
        setSelectedOptions({});
        setQuantity(1);

        onClose();
        handleClose(); // Trigger slide-down on submit
    };

    // console.log('selectedOptions at 430 >> ', selectedOptions);


    return (

        // <div className="z-10 fixed inset-0 flex items-end justify-center bg-black bg-opacity-50 ">
        //     <div
        //         style={{ height: '85%' }}
        //         className={`bg-white w-full  pt-1 px-4 pb-50 rounded-t-lg transform transition-transform
        //             ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}
        //             `}
        //     >

        <div className="z-20 fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" >
            <div
                ref={modalRef}
                style={{ height: '70%' }}
                className={`bg-white w-11/12 max-w-lg mx-auto p-2 pt-4 rounded-lg transform transition-transform 
      ${isClosing ? 'animate-pop-down' : 'animate-pop-up'}
    `}
            >
                <div className='w-full flex flex-col md:flex-col justify-between items-center '>

                    <div className=' w-full flex flex-col justify-between items-center'>

                        <h2 className="text-xl font-bold mb-1">
                            {product.name.toUpperCase()}
                            {product.description && (
                                <> &nbsp;{product.description.toUpperCase()}</>
                            )}
                            {/* {cartItem?.productKey} */}
                            {/* {cartItem?.options.add_ons && cartItem?.options.add_ons.map((opt: any) => opt.name)} */}
                        </h2>
                        {error && <p className="text-red-500">{error}</p>}

                        {/* <div className="rounded p-1 ">
                            <animated.div style={props} className="flex justify-center" ref={ref}>
                                <img
                                    src={product.image_path && product.image_path.length > 0 ? product.image_path : '../images/df.png'}
                                    alt={product.name}
                                    crossOrigin="anonymous"
                                    className="h-16 md:h-16 object-cover rounded-full"
                                />
                            </animated.div>
                        </div> */}
                        <div className='flex flex-row justify-between'>
                            {/* {isEditMode && ( */}
                            <p className="text-xs text-red-500">
                                {/* ( {isEditMode ? 'Edit Mode' : 'Add Mode'})
                                ( {fromCart ? 'fromCart Mode' : 'NOT fromCart Mode'})
                                ( {hasAddOns ? 'hasAddOns' : 'NO hasAddOns'})
                                ( {hasToppings ? 'hasToppings' : 'NO hasToppings'})
                                ( {specifications ? 'spec' : 'NO spec'}) */}
                            </p>
                            {/* )} */}

                            {/* <p className='mx-2 my-2 font-semibold'>{product.name.toUpperCase()}</p> */}
                            <p className='mx-2 my-2 text-lg font-bold'>{settings?.currencyName}{product.price}</p>
                        </div>

                    </div>

                    {(addOns && addOns.length > 0) ||
                        (toppings && toppings.length > 0) ||
                        (specifications && specifications.length > 0) ? (

                        <div className=' bg-white  w-full md:w-full  h-72 md:h-96 shadow md:shadow-none flex flex-row md:flex-row text-left mt-0 md:mt-4  pb-10 md:pb-28'>

                            <div className="w-full mx-4 my-2 px-2 py-2 overflow-x-hidden overflow-y-scroll ">

                                <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-2">

                                    {/* Render product options */}
                                    {/* <h3 className='font-semibold'>Add-ons</h3> */}
                                    {addOns && addOns.length > 0 && (
                                        <ul className='w-full my-0 font-semibold'>ADD-ON
                                            {addOns.map((option: any) => (
                                                <li key={option.name} className='my-1 font-normal'>
                                                    <label className="flex items-center cursor-pointer">

                                                        <input
                                                            name="add_ons"
                                                            type={product.add_ons_type === 'checkbox' ? 'checkbox' : 'radio'}

                                                            checked={selectedOptions.add_ons ? selectedOptions.add_ons.some((opt: any) => opt.name === option.name) : false}

                                                            onChange={() => handleOptionChange('add_ons', product.id, option.id, option.name, option.price, product.add_ons_type)}
                                                            className='mr-2'
                                                        />
                                                        {option.name} (+{settings?.currencyName}{option.price})
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {/* <h4 className='my-2 font-semibold'>Toppings</h4> */}
                                    {toppings && toppings.length > 0 && (
                                        <ul className='w-full my-0 font-semibold'>TOPPINGS
                                            {toppings.map((option: any) => (
                                                <li key={option.name} className='my-1 font-normal'>
                                                    <label className="flex items-center cursor-pointer">

                                                        <input
                                                            name="toppings"
                                                            type={product.toppings_type === 'checkbox' ? 'checkbox' : 'radio'}
                                                            checked={selectedOptions.toppings ? selectedOptions.toppings.some((opt: any) => opt.name === option.name) : false}
                                                            onChange={() => handleOptionChange('toppings', product.id, option.id, option.name, option.price, product.toppings_type)}
                                                            className='mr-2'

                                                        />
                                                        {option.name} (+{settings?.currencyName}{option.price})
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {/* Render specifications if available */}
                                {specifications && specifications.length > 0 && (
                                    // <ul className='w-1/2 my-2 font-semibold flex flex-row'>Specifications
                                    <div className='w-full my-0 font-semibold flex flex-col md:flex-row'>
                                        {specifications.map(spec => (
                                            <ul key={spec.id} className='w-full my-1'>
                                                <li>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <h4 className='my-0 font-semibold'>
                                                            {/* {spec.id}. */}
                                                            {spec.name.toUpperCase()}
                                                        </h4>
                                                    </div>
                                                    <ul>
                                                        {spec.options.map((option: any) => (
                                                            <li key={option.option_id} className='my-1 font-normal'>
                                                                <label className="flex items-center cursor-pointer">

                                                                    <input
                                                                        name={spec.name} // Group radio buttons by specification name
                                                                        type={spec.type === 'checkbox' ? 'checkbox' : 'radio'}
                                                                        checked={selectedOptions.specifications
                                                                            ? selectedOptions.specifications.some(
                                                                                (opt: any) => opt.name === option.option_name
                                                                            )
                                                                            : false
                                                                        }
                                                                        onChange={() => handleOptionChange(
                                                                            'specifications',
                                                                            spec.id,
                                                                            option.option_id,
                                                                            option.option_name,
                                                                            option.extra_cost,
                                                                            spec.type
                                                                        )}
                                                                        className='mr-2'
                                                                    />
                                                                    {/* {option.option_id}. */}
                                                                    {option.option_name} (+{settings?.currencyName}{option.extra_cost})
                                                                </label>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </li>
                                            </ul>
                                        ))}
                                    </div>
                                    // </ul>
                                )}

                            </div>
                        </div>
                    ) : ''}
                </div>


                <div className='w-full flex flex-col justify-between px-4 pb-2 text-left fixed bottom-2 left-0 bg-white'>

                    <div className='flex flex-row justify-between md:justify-between'>
                        <div className='flex flex-col md:flex-row justify-start px-4 py-2'>

                            <p className={`${isEditMode ? 'text-green-800 font-bold' : 'text-black'} text-sm mt-2 mx-2`}>{isEditMode ? 'Change' : 'Add'} Quantity:</p>

                            <div className='flex flex-row'>
                                <button className="AdjBtn"
                                    // onClick={() => setQuantity(quantity - 1)}
                                    onClick={() => setQuantity(prev => Math.max(prev - 1, 1))} // Prevent going below 1

                                >
                                    -
                                </button>

                                <p className='mt-2 mx-2'>
                                    {quantity}
                                    {/* {isEditMode ? quantity : 1} */}
                                    {/* Quantity: {quantity} */}
                                </p>

                                <button className="AdjBtn"
                                    // onClick={() => setQuantity(quantity + 1)}
                                    onClick={() => setQuantity(prev => prev + 1)} // Increase quantity

                                >
                                    +
                                </button>
                            </div>
                        </div>
                        <h3 className='text-xl text-green-700 font-bold mt-4 md:mt-0 pt-4'>Total Price: {settings?.currencyName}{displayPrice(totalPrice)}</h3>

                    </div>

                    <div className='flex flex-row justify-center mt-2'>

                        <button onClick={handleSubmit} className={` ${isEditMode ? 'GoBtn' : 'ActBtn'}`}>
                            {isEditMode ? 'Update' : 'Add to Cart'}
                        </button>
                        <button onClick={handleClose} className="CancelBtn">
                            Cancel
                        </button>

                    </div>


                </div>

            </div>
        </div>
    );
};

export default ProductOptionsModal;
